import type { ReactNode } from "react";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { cn } from "src/lib/utils";
import { Field, type FieldProps } from "./Field";

export interface SelectOption {
	label: ReactNode;
	value: string | number;
}

export interface SelectFieldProps extends Pick<FieldProps, "label" | "error" | "hint"> {
	name?: string;
	required?: boolean;
	disabled?: boolean;
	options?: SelectOption[];
	value?: string | number | null;
	/** Receives the selected option's value as a string. */
	onChange?: (value: string) => void;
	placeholder?: string;
	/** Shown in the dropdown when `options` is empty. */
	emptyMessage?: string;
	className?: string;
	containerClassName?: string;
	contentClassName?: string;
}

/**
 * Dropdown built on the shadcn `Select`. Values are compared and emitted as strings —
 * Radix only handles string values, so numeric ids are stringified at the boundary.
 */
export const SelectField = ({
	label,
	error,
	hint,
	name,
	required,
	disabled,
	options = [],
	value,
	onChange,
	placeholder = "Select an option",
	emptyMessage = "No options available",
	className,
	containerClassName,
	contentClassName,
}: SelectFieldProps) => {
	const selected = options.find((option) => String(option.value) === String(value));

	return (
		<Field label={label} name={name} required={required} error={error} hint={hint} className={containerClassName}>
			<Select value={selected ? String(selected.value) : undefined} onValueChange={onChange} disabled={disabled} name={name}>
				<SelectTrigger
					id={name}
					aria-invalid={!!error}
					className={cn(
						"tw-h-11 tw-w-full",
						!selected && "tw-text-text-soft",
						error && "tw-border-red focus:tw-border-red focus:tw-ring-red/20",
						className
					)}
				>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>

				<SelectContent className={cn("tw-min-w-[var(--radix-select-trigger-width)]", contentClassName)}>
					{options.length ? (
						options.map((option) => (
							<SelectItem key={String(option.value)} value={String(option.value)} className="tw-cursor-pointer">
								{option.label}
							</SelectItem>
						))
					) : (
						<p className="tw-px-2 tw-py-3 tw-text-center tw-text-[13px] tw-text-text-soft">{emptyMessage}</p>
					)}
				</SelectContent>
			</Select>
		</Field>
	);
};

export default SelectField;
